import React, { useState } from 'react';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Plus, X, Save, Check } from 'lucide-react';

const RHStudyForm = ({ onNavigate, onStudyCreated }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [studyId, setStudyId] = useState(null);
  const [error, setError] = useState('');

  // Step 1 — Identification
  const [form, setForm] = useState({
    title: '',
    level: 'R2',
    domain: 'humain',
    sub_domain: '',
    location: '',
    period_start: '',
    period_end: '',
    population_size: ''
  });

  // Step 2 — Chaîne causale
  const [chainLinks, setChainLinks] = useState([
    { description: '', source: '', source_type: 'official', data_value: '', data_unit: '' }
  ]);

  const token = localStorage.getItem('oracle_token');
  const headers = { Authorization: `Bearer ${token}` };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // --- Step 1 : Créer l'étude ---
  const saveStep1 = async () => {
    if (!form.title.trim()) { setError('Le titre est obligatoire'); return; }
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        population_size: form.population_size ? parseInt(form.population_size) : null
      };
      const res = await axios.post('/api/rh/studies', body, { headers });
      const id = res.data.id || res.data.study?.id;
      setStudyId(id);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur création étude');
    } finally {
      setSaving(false);
    }
  };

  // --- Step 2 : Sauvegarder les maillons ---
  const saveStep2 = async () => {
    const validLinks = chainLinks.filter(l => l.description.trim() && l.source.trim());
    if (validLinks.length === 0) { setError('Ajoutez au moins un maillon avec description et source'); return; }
    setSaving(true);
    setError('');
    try {
      for (let i = 0; i < validLinks.length; i++) {
        const link = validLinks[i];
        await axios.post(`/api/rh/studies/${studyId}/chain`, {
          chain_order: i + 1,
          description: link.description,
          source: link.source,
          source_type: link.source_type,
          data_value: link.data_value ? parseFloat(link.data_value) : null,
          data_unit: link.data_unit || null
        }, { headers });
      }
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur sauvegarde chaîne causale');
    } finally {
      setSaving(false);
    }
  };

  // --- Chaîne causale helpers ---
  const addLink = () => {
    setChainLinks(prev => [...prev, { description: '', source: '', source_type: 'official', data_value: '', data_unit: '' }]);
  };

  const removeLink = (index) => {
    if (chainLinks.length <= 1) return;
    setChainLinks(prev => prev.filter((_, i) => i !== index));
  };

  const updateLink = (index, field, value) => {
    setChainLinks(prev => prev.map((link, i) => i === index ? { ...link, [field]: value } : link));
  };

  // --- Rendu ---
  return (
    <div className="p-6 max-w-3xl mx-auto h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => onNavigate('rh')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nouvelle étude RH</h1>
          <p className="text-sm text-gray-500">Protocole 7bis — Rendement Humain</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8">
        {[
          { num: 1, label: 'Identification' },
          { num: 2, label: 'Chaîne causale' },
          { num: 3, label: 'Récapitulatif' }
        ].map((s) => (
          <div key={s.num} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              step === s.num ? 'bg-blue-600 text-white' :
              step > s.num ? 'bg-green-500 text-white' :
              'bg-gray-200 text-gray-500'
            }`}>
              {step > s.num ? <Check size={16} /> : s.num}
            </div>
            <span className={`text-sm ${step === s.num ? 'font-medium text-gray-900' : 'text-gray-400'}`}>
              {s.label}
            </span>
            {s.num < 3 && <div className="flex-1 h-px bg-gray-200 mx-2" />}
          </div>
        ))}
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {/* ===== STEP 1 : Identification ===== */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre de l'étude *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateForm('title', e.target.value)}
              placeholder="Ex: Temps de vie perdu par surcoût logement"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Niveau *</label>
              <select
                value={form.level}
                onChange={(e) => updateForm('level', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="R1">R1 — Individuel</option>
                <option value="R2">R2 — Ménage</option>
                <option value="R3">R3 — Local</option>
                <option value="R4">R4 — National</option>
                <option value="R5">R5 — Systémique</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domaine *</label>
              <select
                value={form.domain}
                onChange={(e) => updateForm('domain', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="humain">Humain</option>
                <option value="ecologique">Écologique</option>
                <option value="culturel">Culturel</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sous-domaine</label>
            <input
              type="text"
              value={form.sub_domain}
              onChange={(e) => updateForm('sub_domain', e.target.value)}
              placeholder="Ex: logement, transport, alimentation..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Début période</label>
              <input
                type="date"
                value={form.period_start}
                onChange={(e) => updateForm('period_start', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fin période</label>
              <input
                type="date"
                value={form.period_end}
                onChange={(e) => updateForm('period_end', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => updateForm('location', e.target.value)}
                placeholder="Ex: France"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Population</label>
              <input
                type="number"
                value={form.population_size}
                onChange={(e) => updateForm('population_size', e.target.value)}
                placeholder="Taille de la population"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={saveStep1}
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Création...' : 'Créer et continuer'}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ===== STEP 2 : Chaîne causale ===== */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Chaîne causale</h2>
            <span className="text-sm text-gray-500">{chainLinks.length} maillon{chainLinks.length > 1 ? 's' : ''}</span>
          </div>

          <div className="space-y-4">
            {chainLinks.map((link, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-blue-600">Maillon #{index + 1}</span>
                  {chainLinks.length > 1 && (
                    <button
                      onClick={() => removeLink(index)}
                      className="p-1 hover:bg-blue-50 rounded text-blue-400 hover:text-blue-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
                    <input
                      type="text"
                      value={link.description}
                      onChange={(e) => updateLink(index, 'description', e.target.value)}
                      placeholder="Description du maillon causal"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Source *</label>
                      <input
                        type="text"
                        value={link.source}
                        onChange={(e) => updateLink(index, 'source', e.target.value)}
                        placeholder="Référence (INSEE, étude...)"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type de source</label>
                      <select
                        value={link.source_type}
                        onChange={(e) => updateLink(index, 'source_type', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="official">Officielle</option>
                        <option value="academic">Académique</option>
                        <option value="empirical">Empirique</option>
                        <option value="calculated">Calculée</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Valeur</label>
                      <input
                        type="number"
                        step="any"
                        value={link.data_value}
                        onChange={(e) => updateLink(index, 'data_value', e.target.value)}
                        placeholder="Valeur numérique"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Unité</label>
                      <input
                        type="text"
                        value={link.data_unit}
                        onChange={(e) => updateLink(index, 'data_unit', e.target.value)}
                        placeholder="€, %, heures..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Flèche entre maillons */}
                {index < chainLinks.length - 1 && (
                  <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 text-gray-400 text-lg">↓</div>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={addLink}
            className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            <Plus size={16} />
            Ajouter un maillon
          </button>

          <div className="flex justify-between pt-6 mt-4 border-t border-gray-100">
            <button
              onClick={() => setStep(1)}
              disabled
              className="flex items-center gap-2 text-gray-400 px-4 py-2 cursor-not-allowed"
              title="L'étude est déjà créée"
            >
              <ArrowLeft size={16} />
              Retour
            </button>
            <button
              onClick={saveStep2}
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Sauvegarde...' : 'Enregistrer la chaîne'}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ===== STEP 3 : Récapitulatif ===== */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Étude créée avec succès</h2>
            <p className="text-sm text-gray-500 mt-1">Brouillon sauvegardé avec {chainLinks.filter(l => l.description.trim()).length} maillon(s)</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2 text-sm">
            <div><span className="font-medium text-gray-700">Titre :</span> {form.title}</div>
            <div><span className="font-medium text-gray-700">Niveau :</span> {form.level}</div>
            <div><span className="font-medium text-gray-700">Domaine :</span> {form.domain}</div>
            {form.sub_domain && <div><span className="font-medium text-gray-700">Sous-domaine :</span> {form.sub_domain}</div>}
            {form.location && <div><span className="font-medium text-gray-700">Localisation :</span> {form.location}</div>}
            {form.period_start && <div><span className="font-medium text-gray-700">Période :</span> {form.period_start} → {form.period_end}</div>}
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Prochaines étapes : ouvrez l'étude pour remplir les <strong>scores Bradford-Hill</strong> (9 critères)
            puis <strong>calculer le RH</strong> avec les valeurs RTE, PA et D.
          </p>

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => onNavigate('rh')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Retour à la liste
            </button>
            <button
              onClick={() => onStudyCreated(studyId)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg transition-colors"
            >
              Ouvrir l'étude
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RHStudyForm;
